// Rename recipe: an ordered list of steps (manual deterministic ops + AI
// snapshots) replayed over the imported items to produce proposed names.

import {
  splitExt,
  joinExt,
  toCase,
  findReplace,
  removeText,
  removeChars,
  trimName,
  replaceSeparators,
  padNumber,
  formatNumbered,
} from "@/lib/rename/ops";

export const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

function inScope(step, item) {
  switch (step.scope) {
    case "files":
      return item.type === "file";
    case "folders":
      return item.type === "folder";
    case "selected":
      return Array.isArray(step.ids) && step.ids.includes(item.id);
    case "current":
      return step.parent != null && item.parent === step.parent;
    default:
      return true;
  }
}

// transform a single base name (extension handled by caller)
function transformBase(step, base, item) {
  switch (step.type) {
    case "prefix":
      return `${step.text ?? ""}${base}`;
    case "suffix":
      return `${base}${step.text ?? ""}`;
    case "findReplace":
      return findReplace(base, step);
    case "removeText":
      return removeText(base, step);
    case "removeChars":
      return removeChars(base, step);
    case "trim":
      return trimName(base, step);
    case "replaceSeparators":
      return replaceSeparators(base, step);
    case "case":
      return toCase(base, step.style);
    default:
      return base;
  }
}

function applyPerItem(step, items, state) {
  for (const item of items) {
    if (!inScope(step, item)) continue;
    const cur = state.get(item.id);
    const [base, ext] = splitExt(cur, item.type);
    state.set(item.id, joinExt(transformBase(step, base, item), ext));
  }
}

function applyNumbering(step, items, state) {
  const { start = 1, increment = 1, perFolder = false } = step;
  const groups = new Map();
  for (const item of items) {
    if (!inScope(step, item)) continue;
    const key = perFolder ? item.parent || "" : "__all__";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }
  for (const [, group] of groups) {
    let n = parseInt(start, 10) || 1;
    const inc = parseInt(increment, 10) || 1;
    for (const item of group) {
      const cur = state.get(item.id);
      const [base, ext] = splitExt(cur, item.type);
      const numStr = padNumber(n, step.padding);
      state.set(item.id, joinExt(formatNumbered(base, numStr, step), ext));
      n += inc;
    }
  }
}

function applyAi(step, items, state) {
  const map = step.map || {};
  for (const item of items) {
    if (map[item.id] != null) state.set(item.id, map[item.id]);
  }
}

export function applyStep(step, items, state) {
  if (step.type === "numbering") return applyNumbering(step, items, state);
  if (step.type === "ai") return applyAi(step, items, state);
  return applyPerItem(step, items, state);
}

// Returns Map<id, proposedBaseName>
export function replay(items, recipe) {
  const state = new Map(items.map((i) => [i.id, i.name]));
  for (const step of recipe || []) applyStep(step, items, state);
  return state;
}

const STEP_LABELS = {
  prefix: (s) => `Prefix "${s.text}"`,
  suffix: (s) => `Suffix "${s.text}"`,
  findReplace: (s) => `Replace "${s.find}" → "${s.replaceWith}"`,
  removeText: (s) => `Remove "${s.text}"`,
  removeChars: (s) => `Remove ${s.count} ${s.mode} char(s)`,
  trim: () => `Trim spaces`,
  replaceSeparators: (s) => `Separators ${(s.from || []).join("")} → "${s.to}"`,
  case: (s) => `Case: ${s.style}`,
  numbering: (s) =>
    `Number from ${s.start}${s.perFolder ? " (per folder)" : ""}`,
  ai: (s) => s.label || "AI rename",
};

export function stepLabel(step) {
  const fn = STEP_LABELS[step.type];
  return fn ? fn(step) : step.type;
}
